import type { ReactNode } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { useCreator } from "../../creator/CreatorContext";
import { Header, type HeaderNavItem } from "../Header";
import { Breadcrumb } from "./Breadcrumb";
import { GuardBar } from "./GuardBar";
import { NavigationGuardProvider, useGuardedNavigate } from "./NavigationGuard";
import { RecordLabelProvider } from "./RecordLabel";
import { StudioActionZone } from "./StudioActions";
import { studioChrome, type NavKey } from "./routeMap";
import css from "./studio.module.css";

/**
 * Sits **outside** StudioProvider, so a non-owner is turned away before any
 * `/studio` request is made. Still not the gate - the server checks OWNER_SUB
 * on every request (D-26) - but it keeps the client from asking for something
 * it will be refused, and from showing an error where a redirect belongs.
 */
export function RequireOwner({ children }: { children: ReactNode }) {
  const { me } = useCreator();
  if (!me?.isOwner) return <Navigate to="/tabs" replace />;
  return <>{children}</>;
}

/**
 * The Studio shell - §11.
 *
 * Two things it is **not**: it is not an authorisation boundary (the server
 * checks `OWNER_SUB` on every `/studio/*` request, D-26), and it is not
 * responsive (D-13 - below 1100px it says so rather than pretending).
 *
 * The chrome is **derived from the route** (W-1), never declared per screen.
 * That is what fixes the defect this shell had: it rendered a fixed nav array
 * and no screen told it anything, so inside the asset form all three items
 * appeared unmarked and the author could not tell where they were.
 */

/**
 * Studio's nav items. They render in the app header, in the same slot the
 * patron nav uses - there is one chrome row, not two (handoff Turn 1, 1b).
 */
const STUDIO_NAV: { to: string; label: string; key: NavKey }[] = [
  { to: "/studio", label: "ASSETS", key: "assets" },
  { to: "/studio/tiers", label: "TIERS & ACCESS", key: "tiers" },
  { to: "/studio/lists", label: "LISTS", key: "lists" },
];

export default function StudioShell() {
  return (
    <NavigationGuardProvider>
      <StudioChrome />
    </NavigationGuardProvider>
  );
}

/**
 * Split from `StudioShell` only so the chrome can call `useGuardedNavigate`,
 * which needs the provider above it.
 */
function StudioChrome() {
  return (
    /*
     * Wraps the whole chrome, not just the outlet: the breadcrumb is computed
     * from the label, so the label has to be readable above the thing that
     * renders it (FR-4).
     */
    <RecordLabelProvider>
      {(recordLabel) => <StudioChromeAt recordLabel={recordLabel} />}
    </RecordLabelProvider>
  );
}

function StudioChromeAt({ recordLabel }: { recordLabel?: string }) {
  const { pathname } = useLocation();
  const { me } = useCreator();
  const guardedNavigate = useGuardedNavigate();
  /*
   * `NavLink` cannot express this. Its `isActive` asks "does the URL match my
   * `to`", so `/studio/assets/new` matches nothing and every item renders
   * unmarked. Model 1b's rule is that a child highlights its **parent**, which
   * is a property of the route tree rather than of any one link - so it is
   * answered once, here, by `studioChrome`.
   */
  const chrome = studioChrome(pathname, recordLabel);

  const nav: HeaderNavItem[] = STUDIO_NAV.map((item) => ({
    to: item.to,
    label: item.label,
    active: chrome.activeNav === item.key,
  }));

  /* Handoff: `ADAM · OWNER` on the right, in place of the patron tier badge
     and avatar. A link, not a label - it is Studio's only route to the account
     screen, and a plain span would strand the owner. */
  const owner = (me?.name ?? "").split(/\s+/)[0]?.toUpperCase() ?? "";

  return (
    <>
      {/* §11: the wordmark reads STUDIO in maroon. No other chrome changes. */}
      <Header
        wordmark="STUDIO"
        nav={nav}
        navLabel="Studio"
        onNavigate={guardedNavigate}
        right={
          <Link to="/account" className={css.ownerTag}>
            {owner ? `${owner} · OWNER` : "OWNER"}
          </Link>
        }
      />

      <p className={css.tooNarrow}>
        STUDIO NEEDS A WIDER SCREEN. COME BACK ON A COMPUTER.
      </p>

      <main className={`${css.page} ${css.desktopOnly}`}>
        {/*
          Standard chrome on every route (FR-2), so its absence never has to
          mean anything. The record's own label arrives in task 5, when the
          screen editing it can supply the title; until then a deep route reads
          UNTITLED, which is honest rather than wrong.
        */}
        <Breadcrumb crumbs={chrome.crumbs} />
        <GuardBar />

        {/*
          Every persistence control on every Studio screen comes out here
          (FR-8, FR-9). A screen that declares none gets no bar, so the asset
          list cannot grow a save.
        */}
        <StudioActionZone>
          <Outlet />
          <div className={css.actionBarSpacer} aria-hidden="true" />
        </StudioActionZone>
      </main>
    </>
  );
}
